<!--
  Under 768 px the sidebar hides. A slim top bar keeps the logo, the
  notifications and the photo, and this tab bar keeps four places and More.
  More opens the rest of the sidebar as a sheet (MobileMoreSheet). A new tab
  here also goes in TAB_BAR_KEYS, so the sheet leaves it out.
-->
<template>
  <div
    class="gm-mobile-top md:hidden"
    :class="{ 'has-chip': hasTwoProfiles && isMenteeMode }"
  >
    <InertiaLink
      href="/dashboard"
      class="gm-mobile-logo"
      aria-label="GrowthMentor home"
    >
      <img
        src="/growthmentor-logo.svg"
        alt=""
        class="gm-mobile-wordmark"
        data-allow-mismatch
      />
      <img src="/gm-logomark.svg" alt="" class="gm-mobile-mark" />
    </InertiaLink>
    <!-- A mentor on the Mentee profile: the chip says so and switches back. -->
    <button
      v-if="hasTwoProfiles && isMenteeMode"
      type="button"
      class="gm-mode-chip"
      aria-label="Mentee profile. Switch to Mentor"
      @click="switchMode"
    >
      Mentee
    </button>
    <span class="grow" />
    <InertiaLink
      href="/notifications"
      class="relative grid size-10 place-items-center rounded-full text-neutral-700 hover:bg-neutral-50"
      @click="markNotificationsAsSeen"
    >
      <BellIcon class="size-5.5" aria-hidden="true" />
      <span class="sr-only">Notifications</span>
      <span v-if="counts.notifications" class="gm-tab-count top-0.5 -right-0.5">
        {{ counts.notifications > 99 ? '99+' : counts.notifications }}
      </span>
    </InertiaLink>
    <button
      type="button"
      class="rounded-full focus-visible:ring focus-visible:ring-neutral-200 focus-visible:outline-none"
      aria-label="Open the menu"
      @click="isMoreSheetOpen = true"
    >
      <Avatar :src="currentUser.avatarUrl" disable-lazy-loading />
    </button>
  </div>

  <nav class="gm-tabbar md:hidden" aria-label="Main">
    <InertiaLink
      v-for="tab in tabs"
      :key="tab.key"
      :href="tab.href"
      class="gm-tab"
      :data-tour="`nav-${tab.key}`"
      :class="{
        'is-active': !isMoreSheetOpen && isPathMatch(page.url, tab.match),
      }"
      :aria-current="isPathMatch(page.url, tab.match) ? 'page' : undefined"
      @click="tab.onClick?.()"
    >
      <component :is="tab.icon" class="size-6" aria-hidden="true" />
      <span
        v-if="tab.badge"
        class="gm-tab-count -top-1 left-[calc(50%+0.375rem)]"
      >
        {{ tab.badge > 99 ? '99+' : tab.badge }}
      </span>
      {{ tab.label }}
    </InertiaLink>
    <button
      type="button"
      class="gm-tab"
      :class="{ 'is-active': isMoreSheetOpen }"
      :aria-expanded="isMoreSheetOpen"
      @click="isMoreSheetOpen = !isMoreSheetOpen"
    >
      <MenuIcon class="size-6" aria-hidden="true" />
      <!-- A notice waits in the sheet: the profile state, a team
           invitation, the time zone. -->
      <span v-if="needsAttention" class="gm-tab-dot">
        <span class="sr-only">Something needs you.</span>
      </span>
      More
    </button>
  </nav>
</template>

<script setup lang="ts">
import { computed, markRaw, type Component } from 'vue'
import { usePage } from '@inertiajs/vue3'
import HomeIcon from '~icons/lucide/house'
import SearchIcon from '~icons/lucide/search'
import SessionsIcon from '~icons/lucide/calendar-clock'
import MessagesIcon from '~icons/lucide/message-square-text'
import BellIcon from '~icons/lucide/bell'
import MenuIcon from '~icons/lucide/menu'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import { ACTOR_TYPE } from '@/shared/data/permissionSystem'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { isPathMatch } from './useSidebarNavigation'
import { useSidebarState } from './useSidebarState'
import { useProfileMode } from './useProfileMode'
import { useShellNotices } from './useShellNotices'
import type { SidebarCounts } from './useSidebarCounts'

const props = defineProps<{
  counts: SidebarCounts
  markMessagesAsRead: () => void
  markNotificationsAsSeen: () => void
}>()

type Tab = {
  key: string
  label: string
  icon: Component
  href: string
  match: string[]
  badge?: number | null
  onClick?: () => void
}

const page = usePage()
const { currentUser } = useCurrentUser()
const { accessControl } = usePermissionSystem()
const { isMoreSheetOpen } = useSidebarState()
const { hasTwoProfiles, isMenteeMode, switchMode } = useProfileMode()
const { needsAttention } = useShellNotices()

const sessionsHref = computed(() => {
  if (accessControl.value.actorType === ACTOR_TYPE.MENTOR)
    return '/mentor/sessions'
  if (accessControl.value.actorType === ACTOR_TYPE.MENTEE) return '/sessions'
  return null
})

const tabs = computed(
  () =>
    [
      {
        key: 'home',
        label: 'Home',
        icon: markRaw(HomeIcon),
        href: '/dashboard',
        match: ['/dashboard'],
      },
      {
        key: 'mentors',
        label: 'Mentors',
        icon: markRaw(SearchIcon),
        href: '/search',
        match: ['/search', '/matches', '/lists', '/mentors', '/ai-matching'],
      },
      sessionsHref.value && {
        key: 'sessions',
        label: 'Sessions',
        icon: markRaw(SessionsIcon),
        href: sessionsHref.value,
        match: ['/sessions', '/mentor/sessions'],
        badge: props.counts.pendingSessionRequests,
      },
      {
        key: 'messages',
        label: 'Messages',
        icon: markRaw(MessagesIcon),
        href: '/conversations',
        match: ['/conversations'],
        badge: props.counts.messages,
        onClick: props.markMessagesAsRead,
      },
    ].filter(Boolean) as Tab[],
)
</script>

<style scoped>
.gm-mobile-top {
  container: gm-top / inline-size;
  position: relative;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #fff;
  border-bottom: 1px solid var(--color-neutral-100);
}
.gm-mobile-logo {
  display: flex;
  align-items: center;
  min-width: 0;
}
.gm-mobile-wordmark {
  width: 9rem;
  max-width: 100%;
  height: auto;
}
.gm-mobile-mark {
  display: none;
  width: 0.9375rem;
  height: auto;
}
.gm-mode-chip {
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  background: var(--gm-mode-mentee);
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
}
.gm-mode-chip:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}
.gm-tabbar {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  z-index: 40;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  padding: 0.375rem 0.25rem calc(0.375rem + env(safe-area-inset-bottom, 0px));
  background: rgb(255 255 255 / 0.97);
  border-top: 1px solid var(--color-neutral-100);
  backdrop-filter: blur(8px);
}
/* The display rules above are unlayered, so Tailwind's md:hidden cannot beat
   them. The hide lives here, next to them. */
@media (min-width: 48rem) {
  .gm-mobile-top,
  .gm-tabbar {
    display: none;
  }
}
.gm-tab {
  position: relative;
  display: grid;
  justify-items: center;
  gap: 0.125rem;
  padding: 0.25rem 0;
  border-radius: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--color-neutral-500);
}
.gm-tab.is-active {
  color: var(--color-neutral-900);
  font-weight: 600;
}
.gm-tab:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}
.gm-tab-dot {
  position: absolute;
  top: 0.125rem;
  left: calc(50% + 0.5rem);
  width: 0.5625rem;
  height: 0.5625rem;
  border-radius: 999px;
  background: var(--color-error-500);
  box-shadow: 0 0 0 2px #fff;
}
.gm-tab-count {
  position: absolute;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 0.3125rem;
  border-radius: 999px;
  background: var(--color-error-500);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 600;
  display: grid;
  place-items: center;
  box-shadow: 0 0 0 2px #fff;
  font-variant-numeric: tabular-nums;
}
</style>
